import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Settings, BarChart3, MessageSquare, LogOut, User, Wallet, Users } from 'lucide-react';
import UnifiedSettings from '../Settings/UnifiedSettings';
import { useAuthStore, useChatStore } from '../../stores';

const SimpleHeader: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showVoiceSettings, setShowVoiceSettings] = React.useState(false);
  const { user, logout } = useAuthStore();
  const { clearConversations } = useChatStore();

  const handleSettings = () => {
    setShowVoiceSettings(true);
  };
  
  const handleLogout = () => {
    clearConversations(); // Очищаем чаты текущего пользователя
    logout();
    navigate('/login');
  };

  const navigateToChat = () => {
    navigate('/');
  };

  const navigateToStatistics = () => {
    navigate('/statistics');
  };

  const navigateToUsers = () => {
    navigate('/users');
  };

  const isStatisticsPage = location.pathname === '/statistics';
  const isUsersPage = location.pathname === '/users';

  return (
    <header style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem 2rem',
      background: '#ffffff',
      borderBottom: '1px solid #e5e7eb',
      boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      position: 'relative',
      zIndex: 10
    }}>
      <h1 style={{
        margin: 0,
        fontSize: '1.5rem',
        fontWeight: '700',
        color: '#111827',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          background: 'linear-gradient(135deg, #0284c7, #0369a1)',
          borderRadius: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: '700',
          fontSize: '1.125rem'
        }}>
          AI
        </div>
        AI Assistant
      </h1>
      
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem'
      }}>
        <button
          onClick={navigateToChat}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: (!isStatisticsPage && !isUsersPage) ? '#0284c7' : 'transparent',
            color: (!isStatisticsPage && !isUsersPage) ? 'white' : '#6b7280',
            border: (!isStatisticsPage && !isUsersPage) ? 'none' : '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}
        >
          <MessageSquare size={18} />
          Чат
        </button>
        
        <button
          onClick={navigateToStatistics}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: isStatisticsPage ? '#0284c7' : 'transparent',
            color: isStatisticsPage ? 'white' : '#6b7280',
            border: isStatisticsPage ? 'none' : '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}
        >
          <BarChart3 size={18} />
          Статистика
        </button>
        
        {/* Управление пользователями (только для админа) */}
        {user?.role === 'admin' && (
          <button
            onClick={navigateToUsers}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: isUsersPage ? '#0284c7' : 'transparent',
              color: isUsersPage ? 'white' : '#6b7280',
              border: isUsersPage ? 'none' : '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            <Users size={18} />
            Пользователи
          </button>
        )}
        
        {/* Баланс */}
        {user && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            padding: '0.5rem 1rem',
            background: (user.balance ?? 0) > 0 ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : '#fef3c7',
            borderRadius: '0.75rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: (user.balance ?? 0) > 0 ? 'white' : '#92400e',
            marginLeft: '1rem',
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)'
          }}>
            <Wallet size={16} />
            <span>{(user.balance ?? 0).toFixed(2)} ₽</span>
          </div>
        )}
        
        <button
          onClick={handleSettings}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            background: 'transparent',
            color: '#6b7280',
            border: 'none',
            borderRadius: '50%',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out'
          }}
          title="Настройки"
        >
          <Settings size={18} />
        </button>
        
        {user && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: '#f3f4f6',
            borderRadius: '0.75rem',
            fontSize: '0.875rem',
            marginLeft: '0.5rem'
          }}>
            <User size={16} style={{ color: '#6b7280' }} />
            <span style={{ fontWeight: '500', color: '#111827' }}>{user.username}</span>
            {user.role === 'admin' && (
              <span style={{
                padding: '0.25rem 0.5rem',
                background: '#fef3c7',
                color: '#92400e',
                fontSize: '0.75rem',
                fontWeight: '600',
                borderRadius: '0.375rem'
              }}>
                Admin
              </span>
            )}
          </div>
        )}
        
        <button
          onClick={handleLogout}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: 'transparent',
            color: '#dc2626',
            border: '1px solid #fecaca',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}
          title="Выйти из системы"
        >
          <LogOut size={18} />
          Выход
        </button>
      </div>

      {/* Модальное окно с настройками голоса */}
      <UnifiedSettings 
        isOpen={showVoiceSettings} 
        onClose={() => setShowVoiceSettings(false)} 
      />
    </header>
  );
};

export default SimpleHeader;
